// ============================================
// Trading Page - Coin Selection & Buy/Sell Assets
// ============================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { 
  FaArrowLeft, FaSearch, FaHome, FaChartBar, FaEye, FaFileAlt,
  FaArrowUp, FaArrowDown, FaClock, FaDollarSign, FaUserCircle
} from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { getAssetImage } from '../utils/imageHelper';
import io from 'socket.io-client';
import './Trading.css';

// Helper to get socket/API URL based on environment (evaluated at runtime)
const getBackendUrl = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:3001';
    }
  }
  return 'https://api.steaming-th.com';
};

const Trading = () => {
  const { assetId: urlAssetId } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [socket, setSocket] = useState(null);
  const [assets, setAssets] = useState([]);
  const [filteredAssets, setFilteredAssets] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('popular');
  const [selectedAssetId, setSelectedAssetId] = useState(urlAssetId?.toUpperCase() || '');
  const [asset, setAsset] = useState(null);
  const [amount, setAmount] = useState('0');
  const [tradeType, setTradeType] = useState('buy');
  const [duration, setDuration] = useState('1x');
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(null);
  const [activeTrade, setActiveTrade] = useState(null);
  const [tradeSettings, setTradeSettings] = useState(null);
  const [profitLoss, setProfitLoss] = useState(0);
  const [showTradeModal, setShowTradeModal] = useState(false);
  const [tradeAmount, setTradeAmount] = useState('');
  const [maxTrade, setMaxTrade] = useState(10000000); // Default 10 million
  const chartContainerRef = useRef(null);
  const tradingViewWidgetRef = useRef(null);
  const tradingViewScriptLoadedRef = useRef(false);
  const isChartLoadingRef = useRef(false);
  const currentSymbolRef = useRef(null);

  // Default coin data for display
  const defaultCoins = [
    { id: 'BTC', symbol: 'BTC', name: 'Bitcoin', price: 91297.1, priceChange: -1.35 },
    { id: 'ETH', symbol: 'ETH', name: 'Ethereum', price: 3139.69, priceChange: -1.20 },
    { id: 'BNB', symbol: 'BNB', name: 'BNB', price: 896.07, priceChange: -1.41 },
    { id: 'LTC', symbol: 'LTC', name: 'Litecoin', price: 82.59, priceChange: -1.75 },
    { id: 'ADA', symbol: 'ADA', name: 'Cardano', price: 0.4298, priceChange: -3.31 },
    { id: 'XRP', symbol: 'XRP', name: 'Ripple', price: 2.0896, priceChange: -2.05 },
    { id: 'XLM', symbol: 'XLM', name: 'Stellar', price: 0.2501, priceChange: -0.60 },
    { id: 'TRX', symbol: 'TRX', name: 'Tron', price: 0.2867, priceChange: 1.52 },
    { id: 'ETC', symbol: 'ETC', name: 'Ethereum Classic', price: 13.53, priceChange: -2.66 },
    { id: 'LINK', symbol: 'LINK', name: 'Chainlink', price: 14.12, priceChange: -1.74 },
    { id: 'ZRX', symbol: 'ZRX', name: '0x', price: 0.1477, priceChange: -2.44 },
  ];

  const tabs = [
    { id: 'popular', label: 'Popular coins', link: '/trading' },
    { id: 'foreign', label: 'Foreign currency', link: '/foreign-currency' },
    { id: 'international', label: 'International', link: '/international' },
  ];

  // Initialize socket connection
  useEffect(() => {
    const socketUrl = getBackendUrl();
    console.log('Socket connecting to:', socketUrl);
    const newSocket = io(socketUrl, {
      transports: ['websocket'],
      reconnection: true
    });
    
    setSocket(newSocket);

    return () => {
      if (newSocket) newSocket.close();
    };
  }, []);

  // Helper functions
  const getSymbolForAsset = (assetIdOrSymbol) => {
    // First, try to find asset in assets array by ID or symbol
    const allAssets = assets.length > 0 ? assets : defaultCoins;
    const foundAsset = allAssets.find(
      a => a.id?.toString().toLowerCase() === assetIdOrSymbol?.toString().toLowerCase() ||
           a.symbol?.toLowerCase() === assetIdOrSymbol?.toString().toLowerCase()
    );
    
    if (foundAsset) {
      const symbolKey = foundAsset.symbol?.toUpperCase();
      const symbolMap = {
        'BTC': 'BINANCE:BTCUSDT',
        'ETH': 'BINANCE:ETHUSDT',
        'BNB': 'BINANCE:BNBUSDT',
        'XRP': 'BINANCE:XRPUSDT',
        'ADA': 'BINANCE:ADAUSDT',
        'SOL': 'BINANCE:SOLUSDT',
        'DOT': 'BINANCE:DOTUSDT',
        'DOGE': 'BINANCE:DOGEUSDT',
        'MATIC': 'BINANCE:MATICUSDT',
        'AVAX': 'BINANCE:AVAXUSDT',
        'LTC': 'BINANCE:LTCUSDT',
        'XLM': 'BINANCE:XLMUSDT',
        'TRX': 'BINANCE:TRXUSDT',
        'ETC': 'BINANCE:ETCUSDT',
        'LINK': 'BINANCE:LINKUSDT',
        'ZRX': 'BINANCE:ZRXUSDT',
        'XAU': 'FX_IDC:XAUUSD',
        'OIL': 'NYMEX:CL1!'
      };
      return symbolMap[symbolKey] || 'BINANCE:BTCUSDT';
    }
    
    // Fallback to direct mapping
    const symbolMap = {
      'btc': 'BINANCE:BTCUSDT',
      'eth': 'BINANCE:ETHUSDT',
      'bnb': 'BINANCE:BNBUSDT',
      'xrp': 'BINANCE:XRPUSDT',
      'ada': 'BINANCE:ADAUSDT',
      'sol': 'BINANCE:SOLUSDT',
      'dot': 'BINANCE:DOTUSDT',
      'doge': 'BINANCE:DOGEUSDT',
      'matic': 'BINANCE:MATICUSDT',
      'avax': 'BINANCE:AVAXUSDT',
      'ltc': 'BINANCE:LTCUSDT',
      'xlm': 'BINANCE:XLMUSDT',
      'trx': 'BINANCE:TRXUSDT',
      'etc': 'BINANCE:ETCUSDT',
      'link': 'BINANCE:LINKUSDT',
      'zrx': 'BINANCE:ZRXUSDT',
      'xau': 'FX_IDC:XAUUSD',
      'oil': 'NYMEX:CL1!'
    };
    return symbolMap[assetIdOrSymbol?.toString().toLowerCase()] || 'BINANCE:BTCUSDT';
  };

  const loadTradingViewChart = useCallback(() => {
    // Prevent multiple simultaneous calls
    if (isChartLoadingRef.current) {
      console.log('Chart is already loading, skipping...');
      return;
    }
    
    if (!chartContainerRef.current) {
      console.warn('Chart container not ready');
      return;
    }
    
    // Use asset.symbol if available, otherwise try to find from assets array, then fall back to selectedAssetId
    let assetIdOrSymbol = asset?.symbol;
    
    if (!assetIdOrSymbol && selectedAssetId) {
      // Try to find asset from assets array
      const foundAsset = assets.find(
        a => a.id?.toString().toLowerCase() === selectedAssetId.toString().toLowerCase() ||
             a.symbol?.toLowerCase() === selectedAssetId.toString().toLowerCase()
      );
      assetIdOrSymbol = foundAsset?.symbol || selectedAssetId;
    }
    
    if (!assetIdOrSymbol) {
      console.warn('No asset symbol or ID available for chart');
      return;
    }

    const symbol = getSymbolForAsset(assetIdOrSymbol);
    
    // If symbol hasn't changed and widget exists, don't reload
    if (currentSymbolRef.current === symbol && tradingViewWidgetRef.current) {
      console.log('Chart already showing this symbol:', symbol);
      return;
    }
    
    currentSymbolRef.current = symbol;
    isChartLoadingRef.current = true;

    // If widget exists and script is loaded, just change symbol
    if (tradingViewWidgetRef.current && tradingViewScriptLoadedRef.current && window.TradingView) {
      try {
        // Clear previous price extraction interval
        if (tradingViewWidgetRef.current._priceExtractionInterval) {
          clearInterval(tradingViewWidgetRef.current._priceExtractionInterval);
        }
        
        tradingViewWidgetRef.current.setSymbol(symbol, '1', () => {
          console.log('Chart symbol updated to:', symbol);
          isChartLoadingRef.current = false;
          // Restart price extraction for new symbol
          if (symbol === 'FX_IDC:XAUUSD' || symbol.includes('XAU')) {
            setTimeout(() => {
              // Price extraction will be set up in onChartReady
            }, 1000);
          }
        });
        return;
      } catch (error) {
        console.log('Error updating symbol, will recreate widget:', error);
        // If update fails, we'll recreate below
        if (tradingViewWidgetRef.current?._priceExtractionInterval) {
          clearInterval(tradingViewWidgetRef.current._priceExtractionInterval);
        }
        tradingViewWidgetRef.current = null;
      }
    }

    // Only clear if we need to create new widget
    if (chartContainerRef.current && !tradingViewWidgetRef.current) {
      chartContainerRef.current.innerHTML = '';
    }

    // Load script if not already loaded
    if (!tradingViewScriptLoadedRef.current) {
      const existingScript = document.querySelector('script[src="https://s3.tradingview.com/tv.js"]');
      if (!existingScript) {
        console.log('Loading TradingView script...');
        const script = document.createElement('script');
        script.src = 'https://s3.tradingview.com/tv.js';
        script.async = true;
        script.onload = () => {
          console.log('TradingView script loaded');
          tradingViewScriptLoadedRef.current = true;
          if (chartContainerRef.current && symbol && !tradingViewWidgetRef.current) {
            createTradingViewWidget(symbol);
          }
        };
        script.onerror = () => {
          console.error('Failed to load TradingView script');
        };
        document.head.appendChild(script);
      } else {
        // Script exists, wait a bit and create widget
        tradingViewScriptLoadedRef.current = true;
        setTimeout(() => {
          if (chartContainerRef.current && symbol && !tradingViewWidgetRef.current) {
            createTradingViewWidget(symbol);
          } else {
            isChartLoadingRef.current = false;
          }
        }, 100);
        return;
      }
    } else {
      // Script already loaded, create widget directly
      if (window.TradingView && chartContainerRef.current && symbol && !tradingViewWidgetRef.current) {
        createTradingViewWidget(symbol);
      } else {
        isChartLoadingRef.current = false;
      }
    }
  }, [selectedAssetId, asset, assets, getSymbolForAsset]);

  const createTradingViewWidget = (symbol) => {
    console.log('createTradingViewWidget called', {
      TradingView: !!window.TradingView,
      chartContainer: !!chartContainerRef.current,
      symbol: symbol
    });
    
    if (!window.TradingView) {
      console.error('TradingView not available');
      return;
    }
    
    if (!chartContainerRef.current) {
      console.error('Chart container not available');
      return;
    }
    
    if (!symbol) {
      console.error('Symbol not provided');
      return;
    }
    
    console.log('Creating TradingView widget with symbol:', symbol);

    const widget = new window.TradingView.widget({
      autosize: true,
      symbol: symbol,
      interval: '1',
      timezone: 'Asia/Bangkok',
      theme: 'dark',
      style: '1',
      locale: 'th_TH',
      toolbar_bg: '#1a1a1a',
      enable_publishing: false,
      hide_side_toolbar: true,
      allow_symbol_change: false,
      hide_top_toolbar: true,
      hide_legend: false,
      container_id: 'tradingview_chart',
      studies: [
        'MASimple@tv-basicstudies'
      ],
      backgroundColor: '#1a1a1a',
      gridLineColor: '#2a2a2a',
      lineColor: '#667eea',
      overrides: {
        'paneProperties.background': '#1a1a1a',
        'paneProperties.backgroundType': 'solid',
        'scalesProperties.showVolume': false,
        'volumePaneSize': 'tiny',
        'mainSeriesProperties.volumeStyle.visible': false,
        'mainSeriesProperties.volumeStyle.volumeMa.visible': false
      },
          disabled_features: [
            'volume_force_overlay',
            'use_localstorage_for_settings',
            'show_logo_on_all_charts',
            'left_toolbar',
            'header_widget',
            'header_widget_dom_node',
            'header_symbol_search',
            'symbol_search_hot_key',
            'display_market_status',
            'border_around_the_chart',
            'header_chart_type',
            'header_compare',
            'header_undo_redo',
            'header_screenshot',
            'header_screenshot_hide_all_studies',
            'header_screenshot_hide_series',
            'use_localstorage_for_settings',
            'header_resolutions',
            'header_widget_dom_node',
            'context_menus',
            'control_bar',
            'timeframes_toolbar',
            'edit_buttons_in_legend',
            'show_logo_on_all_charts',
            'show_interval_dialog_on_key_press',
            'show_dialog_on_snapshot_ready',
            'header_saveload',
            'header_settings',
            'header_chart_type',
            'header_compare',
            'header_undo_redo',
            'header_screenshot',
            'header_widget_dom_node',
            'header_symbol_search',
            'symbol_search_hot_key',
            'header_resolutions',
            'header_widget',
            'timeframes_toolbar'
          ],
          enabled_features: [
            'hide_left_toolbar_by_default',
            'hide_header_widget'
          ],
          onChartReady: function(chart) {
            // Store widget reference
            tradingViewWidgetRef.current = widget;
            isChartLoadingRef.current = false;
            
            console.log('TradingView widget ready, chart:', chart, 'symbol:', symbol);
            
            // Extract price from TradingView widget for XAU
            if (symbol === 'FX_IDC:XAUUSD' || symbol.includes('XAU')) {
              console.log('Setting up price extraction for XAU from TradingView widget');
              
              // Function to extract price from DOM
              const extractPriceFromDOM = () => {
                try {
                  const chartContainer = document.getElementById('tradingview_chart');
                  if (!chartContainer) return;
                  
                  // Method 1: Try to find price in visible DOM elements
                  const allElements = chartContainer.querySelectorAll('*');
                  const pricePattern = /^(\d{1,4}(?:[.,]\d{3})*(?:[.,]\d{2})?)$/;
                  
                  for (const el of allElements) {
                    // Skip hidden elements
                    const style = window.getComputedStyle(el);
                    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
                      continue;
                    }
                    
                    // Skip if element is too small (likely not a price display)
                    if (el.offsetHeight < 10 || el.offsetWidth < 20) {
                      continue;
                    }
                    
                    const text = el.textContent?.trim();
                    if (!text) continue;
                    
                    // Check if text matches price pattern
                    const match = text.match(pricePattern);
                    if (match) {
                      const price = parseFloat(match[1].replace(/,/g, ''));
                      // XAU price should be between 1000-3000
                      if (price >= 1000 && price <= 3000) {
                        // Verify this looks like a price (not just any number)
                        const hasPriceContext = el.className?.toLowerCase().includes('price') ||
                                               el.className?.toLowerCase().includes('quote') ||
                                               el.className?.toLowerCase().includes('value') ||
                                               el.parentElement?.className?.toLowerCase().includes('price');
                        
                        if (hasPriceContext || el.offsetHeight > 20) {
                          console.log('✅ Extracted XAU price from TradingView widget DOM:', price, 'from element:', el.className);
                          if (asset?.symbol === 'XAU') {
                            setAsset(prevAsset => {
                              const prevPrice = prevAsset?.price || price;
                              const priceChange = prevPrice ? ((price - prevPrice) / prevPrice) * 100 : 0;
                              return {
                                ...prevAsset,
                                price: price,
                                priceChange: priceChange
                              };
                            });
                          }
                          return price;
                        }
                      }
                    }
                  }
                  
                  // Method 2: Try to access chart data through chart object
                  try {
                    if (chart && chart.getSeries) {
                      const series = chart.getSeries();
                      if (series && series.getSeriesData) {
                        // Try to get latest price from series data
                        const data = series.getSeriesData();
                        if (data && data.length > 0) {
                          const latestBar = data[data.length - 1];
                          if (latestBar && latestBar.close) {
                            const price = latestBar.close;
                            console.log('✅ Extracted XAU price from TradingView chart data:', price);
                            if (asset?.symbol === 'XAU') {
                              setAsset(prevAsset => ({
                                ...prevAsset,
                                price: price,
                                priceChange: prevAsset?.priceChange || 0
                              }));
                            }
                            return price;
                          }
                        }
                      }
                    }
                  } catch (error) {
                    // Chart API may not be available
                    // console.log('Cannot access chart API:', error);
                  }
                  
                } catch (error) {
                  console.error('Error extracting price from TradingView widget:', error);
                }
              };
              
              // Start extracting price periodically
              const startPriceExtraction = () => {
                // Extract immediately
                extractPriceFromDOM();
                
                // Extract every 2 seconds
                const interval = setInterval(extractPriceFromDOM, 2000);
                widget._priceExtractionInterval = interval;
                
                console.log('✅ Price extraction started for XAU');
              };
              
              // Start extraction after widget fully renders
              setTimeout(startPriceExtraction, 2000);
              setTimeout(startPriceExtraction, 5000);
              
              // Also try on chart updates
              try {
                if (chart.onCrosshairMove) {
                  chart.onCrosshairMove((param) => {
                    if (param && param.time && param.seriesPrices) {
                      const values = Object.values(param.seriesPrices);
                      if (values.length > 0) {
                        const price = values[0];
                        if (price >= 1000 && price <= 3000 && asset?.symbol === 'XAU') {
                          setAsset(prevAsset => ({
                            ...prevAsset,
                            price: price,
                            priceChange: prevAsset?.priceChange || 0
                          }));
                        }
                      }
                    }
                  });
                }
              } catch (error) {
                console.log('Cannot set up crosshair move listener:', error);
              }
            }
            
            // Remove volume pane and hide toolbar/menu after chart is ready
            setTimeout(() => {
              try {
                // Hide volume using chart API
                chart.setVisibleRange({ from: 0, to: 100 });
                
                // Remove volume pane using DOM manipulation
                const chartContainer = document.getElementById('tradingview_chart');
                if (chartContainer) {
                  // Hide/Remove toolbar and menu elements
                  const toolbarSelectors = [
                    '[class*="toolbar"]',
                    '[class*="header"]',
                    '[data-name="header"]',
                    '[class*="tradingview-widget"] > div > div:first-child',
                    '[class*="widgetbar"]',
                    '[class*="control-bar"]',
                    '[class*="button"]',
                    '[class*="icon"]',
                    '[class*="search"]',
                    '[class*="symbol"]',
                    '[class*="timeframe"]',
                    '[class*="interval"]',
                    '[class*="resolution"]',
                    '[class*="chart-type"]',
                    '[class*="compare"]'
                  ];
                  
                  toolbarSelectors.forEach(selector => {
                    const elements = chartContainer.querySelectorAll(selector);
                    elements.forEach(el => {
                      // Check if element is toolbar/menu related
                      const text = el.textContent || '';
                      const className = el.className || '';
                      if (
                        className.includes('toolbar') ||
                        className.includes('header') ||
                        className.includes('widgetbar') ||
                        className.includes('control') ||
                        className.includes('search') ||
                        className.includes('symbol') ||
                        className.includes('timeframe') ||
                        text.includes('Search') ||
                        text.includes('ค้นหา') ||
                        (el.offsetHeight < 100 && (className.includes('button') || className.includes('icon')))
                      ) {
                        el.style.display = 'none';
                        el.style.visibility = 'hidden';
                        el.style.height = '0';
                        el.style.overflow = 'hidden';
                      }
                    });
                  });
                  
                  // Hide the first child if it's a header/toolbar
                  if (chartContainer.firstElementChild) {
                    const firstChild = chartContainer.firstElementChild;
                    const firstChildStyle = window.getComputedStyle(firstChild);
                    if (firstChildStyle.height && parseFloat(firstChildStyle.height) < 100) {
                      firstChild.style.display = 'none';
                    }
                  }
                  
                  // Remove volume panes
                  const volumePanes = chartContainer.querySelectorAll('[data-name="volume"], [class*="volume"], [class*="pane"]');
                  volumePanes.forEach(pane => {
                    const paneElement = pane.closest('[class*="pane"]');
                    if (paneElement && paneElement.textContent && paneElement.textContent.toLowerCase().includes('volume')) {
                      paneElement.style.display = 'none';
                      paneElement.remove();
                    }
                  });
                  
                  // Also try to find and remove volume bars
                  const volumeBars = chartContainer.querySelectorAll('[class*="volume-bar"], [class*="volumeBar"]');
                  volumeBars.forEach(bar => {
                    bar.style.display = 'none';
                    bar.remove();
                  });
                }
              } catch (error) {
                console.log('Error removing volume and toolbar:', error);
              }
            }, 1000);
            
            // Additional cleanup after more time
            setTimeout(() => {
              const chartContainer = document.getElementById('tradingview_chart');
              if (chartContainer) {
                // Aggressive cleanup - remove all toolbar/header elements
                const cleanupToolbar = () => {
                  // Remove any remaining toolbar elements
                  const allElements = chartContainer.querySelectorAll('*');
                  allElements.forEach(el => {
                    const className = el.className?.toString() || '';
                    const text = el.textContent || '';
                    const style = window.getComputedStyle(el);
                    const height = parseFloat(style.height) || 0;
                    const tagName = el.tagName?.toLowerCase() || '';
                    
                    // Hide elements that match toolbar patterns
                    if (
                      className.includes('header') ||
                      className.includes('toolbar') ||
                      className.includes('widgetbar') ||
                      className.includes('topbar') ||
                      className.includes('control-bar') ||
                      className.includes('button-container') ||
                      className.includes('symbol-search') ||
                      className.includes('timeframe') ||
                      className.includes('interval') ||
                      className.includes('chart-type') ||
                      className.includes('screenshot') ||
                      className.includes('camera') ||
                      className.includes('indicator') ||
                      className.includes('tab') ||
                      className.includes('menu') ||
                      className.includes('search') ||
                      className.includes('ticker') ||
                      (height < 60 && height > 0 && (className.includes('button') || className.includes('icon'))) ||
                      (tagName === 'input' && (className.includes('search') || el.type === 'search')) ||
                      (tagName === 'button' && (height < 50 || className.includes('icon'))) ||
                      (text.includes('BTCUSDT') || text.includes('USDT') || text.match(/\d+[mhdw]/)) ||
                      (className.includes('js-header') || el.getAttribute('data-name') === 'header')
                    ) {
                      el.style.cssText = 'display: none !important; visibility: hidden !important; height: 0 !important; width: 0 !important; opacity: 0 !important; position: absolute !important; left: -9999px !important;';
                      el.remove();
                    }
                  });
                  
                  // Hide first child if it's small (likely toolbar)
                  if (chartContainer.firstElementChild) {
                    const firstChild = chartContainer.firstElementChild;
                    const firstChildStyle = window.getComputedStyle(firstChild);
                    const firstHeight = parseFloat(firstChildStyle.height) || 0;
                    if (firstHeight < 100 && firstHeight > 0) {
                      firstChild.style.cssText = 'display: none !important; visibility: hidden !important; height: 0 !important;';
                      firstChild.remove();
                    }
                  }
                };
                
                // Run cleanup multiple times
                cleanupToolbar();
                setTimeout(cleanupToolbar, 500);
                setTimeout(cleanupToolbar, 1000);
                setTimeout(cleanupToolbar, 2000);
                
                // Monitor for new toolbar elements
                const observer = new MutationObserver(() => {
                  cleanupToolbar();
                });
                observer.observe(chartContainer, { 
                  childList: true, 
                  subtree: true,
                  attributes: true
                });
                
                // Stop observing after 10 seconds
                setTimeout(() => {
                  observer.disconnect();
                }, 10000);
              }
            }, 2000);
          }
        });

    // Additional cleanup after widget loads
    setTimeout(() => {
      const chartContainer = document.getElementById('tradingview_chart');
      if (chartContainer) {
        // Remove volume-related elements
        const observer = new MutationObserver(() => {
          const volumeElements = chartContainer.querySelectorAll('[class*="volume"], [data-name="volume"]');
          volumeElements.forEach(el => {
            if (el.textContent && el.textContent.toLowerCase().includes('volume')) {
              el.style.display = 'none';
              el.remove();
            }
          });
        });
        observer.observe(chartContainer, { childList: true, subtree: true });
      }
    }, 2000);
  };

  // Load script on mount if not already loaded
  useEffect(() => {
    if (!tradingViewScriptLoadedRef.current) {
      const existingScript = document.querySelector('script[src="https://s3.tradingview.com/tv.js"]');
      if (!existingScript && typeof window !== 'undefined' && !window.TradingView) {
        const script = document.createElement('script');
        script.src = 'https://s3.tradingview.com/tv.js';
        script.async = true;
        script.onload = () => {
          tradingViewScriptLoadedRef.current = true;
        };
        document.head.appendChild(script);
      } else if (window.TradingView) {
        tradingViewScriptLoadedRef.current = true;
      }
    }
  }, []);

  const handleTradeComplete = useCallback(async () => {
    if (!activeTrade) return;
    
    toast.info('การเทรดสิ้นสุด กำลังตรวจสอบผล...');
    
    setTimeout(async () => {
      await refreshUser();
      setActiveTrade(null);
      setCountdown(null);
      setAmount('250');
      toast.success('อัพเดทยอดเงินเรียบร้อย');
    }, 2000);
  }, [activeTrade, refreshUser]);

  // Fetch all asset prices from Binance API (real-time updates)
  // Fetch price from TradingView Symbol API
  const fetchTradingViewPrice = async (tradingViewSymbol) => {
    try {
      // For XAU (Gold), use TradingView REST API to get exact price from chart
      if (tradingViewSymbol === 'FX_IDC:XAUUSD') {
        try {
          // Note: TradingView widget uses FX_IDC:XAUUSD which is XAU/USD spot price
          // Yahoo Finance GC=F is Gold Futures, which is close but may differ slightly
          // We'll use GC=F as it's the most accessible real-time data source
          
          // Method 3: Use Yahoo Finance with GC=F (Gold Futures - matches TradingView data)
          try {
            const yahooResponse = await fetch(`https://query1.finance.yahoo.com/v8/finance/chart/GC=F?interval=1m&range=1d&includePrePost=false`, {
              method: 'GET',
              headers: {
                'Accept': 'application/json',
                'User-Agent': 'Mozilla/5.0'
              }
            });
            
            if (yahooResponse.ok) {
              const yahooData = await yahooResponse.json();
              console.log('Yahoo Finance response for GC=F:', yahooData);
              
              if (yahooData.chart && yahooData.chart.result && yahooData.chart.result[0]) {
                const result = yahooData.chart.result[0];
                const meta = result.meta;
                
                // Get the latest price - prioritize regularMarketPrice
                let currentPrice = meta?.regularMarketPrice;
                
                // If not available, get from quote data
                if (!currentPrice && result.indicators?.quote?.[0]) {
                  const quote = result.indicators.quote[0];
                  const closes = quote.close;
                  if (closes && closes.length > 0) {
                    // Get last non-null value
                    for (let i = closes.length - 1; i >= 0; i--) {
                      if (closes[i] != null && closes[i] > 0) {
                        currentPrice = closes[i];
                        break;
                      }
                    }
                  }
                }
                
                // Fallback to previous close
                if (!currentPrice) {
                  currentPrice = meta?.previousClose || meta?.chartPreviousClose;
                }
                
                const previousPrice = meta?.previousClose || meta?.chartPreviousClose || (currentPrice ? currentPrice * 0.999 : null);
                const changePercent = previousPrice && currentPrice && previousPrice > 0
                  ? ((currentPrice - previousPrice) / previousPrice) * 100 
                  : 0;
                
                if (currentPrice && currentPrice > 0) {
                  console.log('✅ XAU price fetched from Yahoo Finance (GC=F):', currentPrice, 'Previous:', previousPrice, 'Change:', changePercent.toFixed(2) + '%');
                  return { price: currentPrice, priceChange: changePercent };
                } else {
                  console.warn('❌ XAU price is invalid:', currentPrice);
                }
              } else {
                console.warn('❌ Invalid Yahoo Finance response structure');
              }
            } else {
              console.error('❌ Yahoo Finance API response not ok:', yahooResponse.status, yahooResponse.statusText);
            }
          } catch (error) {
            console.error('❌ Yahoo Finance API error for XAU:', error);
          }
          
          // Method 4: Try Alpha Vantage API (backup)
          // Note: Requires API key, so skipping for now
        } catch (error) {
          console.error('Error fetching XAU price:', error);
        }
      }
      
      // For OIL (Crude Oil), use Yahoo Finance API
      if (tradingViewSymbol === 'NYMEX:CL1!') {
        try {
          const yahooResponse = await fetch(`https://query1.finance.yahoo.com/v8/finance/chart/CL=F?interval=1d&range=1d`);
          if (yahooResponse.ok) {
            const yahooData = await yahooResponse.json();
            if (yahooData.chart && yahooData.chart.result && yahooData.chart.result[0]) {
              const result = yahooData.chart.result[0];
              const currentPrice = result.meta?.regularMarketPrice || result.meta?.previousClose;
              const previousPrice = result.meta?.previousClose;
              const changePercent = previousPrice > 0 
                ? ((currentPrice - previousPrice) / previousPrice) * 100 
                : 0;
              return { price: currentPrice, priceChange: changePercent };
            }
          }
        } catch (error) {
          console.error('Yahoo Finance API error:', error);
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching TradingView price:', error);
      return null;
    }
  };

  const fetchAllAssetPrices = useCallback(async (assetList) => {
    try {
      const currentAssets = assetList || (assets.length > 0 ? assets : defaultCoins);
      
      // Map of symbol to Binance symbol
      const symbolMap = {
        'BTC': 'BTCUSDT',
        'ETH': 'ETHUSDT',
        'BNB': 'BNBUSDT',
        'XRP': 'XRPUSDT',
        'ADA': 'ADAUSDT',
        'SOL': 'SOLUSDT',
        'DOT': 'DOTUSDT',
        'DOGE': 'DOGEUSDT',
        'MATIC': 'MATICUSDT',
        'AVAX': 'AVAXUSDT',
        'LTC': 'LTCUSDT',
        'XLM': 'XLMUSDT',
        'TRX': 'TRXUSDT',
        'ETC': 'ETCUSDT',
        'LINK': 'LINKUSDT',
        'ZRX': 'ZRXUSDT',
        'XAU': 'FX_IDC:XAUUSD',
        'OIL': 'NYMEX:CL1!'
      };

      const pricePromises = currentAssets.map(async (asset) => {
        const symbolKey = asset.symbol?.toUpperCase();
        const binanceSymbol = symbolMap[symbolKey];
        
        // If it's XAU or OIL, use TradingView/Yahoo Finance API
        if (symbolKey === 'XAU' || symbolKey === 'OIL') {
          const tradingViewSymbol = symbolMap[symbolKey];
          if (tradingViewSymbol) {
            try {
              const priceData = await fetchTradingViewPrice(tradingViewSymbol);
              if (priceData && priceData.price) {
                console.log(`Updated ${symbolKey} price in list:`, priceData.price);
                return {
                  ...asset,
                  price: priceData.price,
                  priceChange: priceData.priceChange
                };
              } else {
                console.warn(`No price data for ${symbolKey}, keeping original price`);
              }
            } catch (error) {
              console.error(`Error fetching price for ${asset.symbol}:`, error);
            }
          }
          return asset;
        }

        // For other assets, use Binance API
        if (!binanceSymbol) {
          return asset;
        }

        try {
          const response = await fetch(`https://api.binance.com/api/v3/ticker/24hr?symbol=${binanceSymbol}`);
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
          const data = await response.json();
          const currentPrice = parseFloat(data.lastPrice);
          const previousPrice = parseFloat(data.prevClosePrice);
          const changePercent = previousPrice > 0 
            ? ((currentPrice - previousPrice) / previousPrice) * 100 
            : 0;
          
          return {
            ...asset,
            price: currentPrice,
            priceChange: changePercent
          };
        } catch (error) {
          console.error(`Error fetching price for ${asset.symbol}:`, error);
          return asset;
        }
      });

      const updatedAssets = await Promise.all(pricePromises);
      
      setAssets(prevAssets => {
        if (JSON.stringify(prevAssets) !== JSON.stringify(updatedAssets)) {
          return updatedAssets;
        }
        return prevAssets;
      });
    } catch (error) {
      console.error('Error fetching asset prices:', error);
    }
  }, [assets]);

  useEffect(() => {
    let assetListCache = [];
    
    loadAssets().then((loadedAssets) => {
      assetListCache = loadedAssets || [];
      if (loadedAssets) {
        fetchAllAssetPrices(loadedAssets);
      }
    });
    loadTradeSettings();
    loadTradingLimits();
    loadProfitLoss();
    
    // Update all asset prices every 2 seconds
    const assetInterval = setInterval(() => {
      const assetsToUpdate = assetListCache.length > 0 
        ? assetListCache 
        : (assets.length > 0 ? assets : defaultCoins);
      fetchAllAssetPrices(assetsToUpdate);
    }, 2000);
    
    return () => {
      clearInterval(assetInterval);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProfitLoss = async () => {
    try {
      const trades = await api.getTradeHistory();
      const totalPL = trades.reduce((sum, trade) => {
        if (trade.status === 'completed') {
          return sum + (trade.profit || 0);
        }
        return sum;
      }, 0);
      setProfitLoss(totalPL);
    } catch (error) {
      console.error('Failed to load P/L:', error);
    }
  };

  // Update selectedAssetId when URL changes
  useEffect(() => {
    console.log('URL assetId changed:', urlAssetId);
    if (urlAssetId) {
      const newSelectedId = urlAssetId.toString().toUpperCase();
      console.log('Setting selectedAssetId to:', newSelectedId);
      setSelectedAssetId(newSelectedId);
    } else {
      // If no assetId in URL, don't clear selectedAssetId (user might be navigating directly)
      console.log('No assetId in URL');
    }
  }, [urlAssetId]);

  useEffect(() => {
    if (selectedAssetId) {
      loadAsset(selectedAssetId);
    }
  }, [selectedAssetId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load chart after asset is loaded
  useEffect(() => {
    if (!selectedAssetId) return;
    
    // Wait a bit for asset to be loaded, but don't wait too long
    const loadChart = () => {
      if (!isChartLoadingRef.current) {
        loadTradingViewChart();
      }
    };
    
    // Small delay to ensure asset state is updated
    const timer = setTimeout(loadChart, 100);
    
    return () => {
      clearTimeout(timer);
    };
  }, [asset?.symbol, selectedAssetId, loadTradingViewChart]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0) {
      handleTradeComplete();
    }
  }, [countdown, handleTradeComplete]);

  // Filter assets based on search
  useEffect(() => {
    const displayAssets = assets.length > 0 ? assets : defaultCoins;
    if (searchQuery.trim() === '') {
      setFilteredAssets(displayAssets);
    } else {
      const filtered = displayAssets.filter(
        (coin) =>
          coin.symbol?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          coin.name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredAssets(filtered);
    }
  }, [searchQuery, assets]);

  // Socket.io listener for trade completion
  useEffect(() => {
    if (!socket) return;

    const handleTradeCompleted = (data) => {
      if (data.userId === user?.id && data.tradeId === activeTrade?.id) {
        const resultMessage = data.status === 'won' 
          ? `🎉 คุณชนะ! กำไร ${data.profit} USD`
          : `😔 คุณแพ้ -${data.amount} USD`;
        
        toast[data.status === 'won' ? 'success' : 'error'](resultMessage);
        
        setTimeout(async () => {
          await refreshUser();
          setActiveTrade(null);
          setCountdown(null);
          setAmount('');
        }, 2000);
      }
    };

    socket.on('trade_completed', handleTradeCompleted);

    return () => {
      socket.off('trade_completed', handleTradeCompleted);
    };
  }, [socket, user, activeTrade, refreshUser]);

  const loadAssets = async () => {
    try {
      const data = await api.getAssets();
      const assetsList = data.length > 0 ? data : defaultCoins;
      setAssets(assetsList);
      setFilteredAssets(assetsList);
      setLoading(false);
      return assetsList;
    } catch (error) {
      setAssets(defaultCoins);
      setFilteredAssets(defaultCoins);
      setLoading(false);
      return defaultCoins;
    }
  };

  const loadTradeSettings = async () => {
    try {
      const apiUrl = `${getBackendUrl()}/api/config/trade-types`;
      console.log('Loading trade settings from:', apiUrl);
      const response = await fetch(apiUrl);
      const data = await response.json();
      console.log('Trade settings loaded:', data);
      setTradeSettings(data);
    } catch (error) {
      console.error('Failed to load trade settings:', error);
    }
  };

  // Load trading limits (minTrade, maxTrade)
  const loadTradingLimits = async () => {
    try {
      const apiUrl = `${getBackendUrl()}/api/config/trading-limits`;
      const response = await fetch(apiUrl);
      const data = await response.json();
      if (data.maxTrade) {
        setMaxTrade(data.maxTrade);
      }
    } catch (error) {
      console.error('Failed to load trading limits:', error);
    }
  };

  const loadAsset = async (id) => {
    try {
      const data = await api.getAsset(id);
      
      // If it's XAU or OIL, fetch real-time price immediately
      const symbolKey = data?.symbol?.toUpperCase();
      if (symbolKey === 'XAU' || symbolKey === 'OIL') {
        const tradingViewSymbol = symbolKey === 'XAU' ? 'FX_IDC:XAUUSD' : 'NYMEX:CL1!';
        try {
          const priceData = await fetchTradingViewPrice(tradingViewSymbol);
          if (priceData && priceData.price) {
            setAsset({
              ...data,
              price: priceData.price,
              priceChange: priceData.priceChange
            });
            return;
          } else {
            setAsset(data);
          }
        } catch (error) {
          console.error('Error fetching real-time price for asset:', error);
          setAsset(data);
        }
      } else {
        setAsset(data);
      }
    } catch (error) {
      console.error('Error loading asset from API:', error);
      // Try to find in assets array
      const foundAsset = assets.find(
        a => a.id?.toString().toLowerCase() === id.toString().toLowerCase() ||
             a.symbol?.toLowerCase() === id.toString().toLowerCase()
      );
      if (foundAsset) {
        setAsset(foundAsset);
      } else {
        // Fallback to defaultCoins
        const defaultCoin = defaultCoins.find(
          c => c.id?.toString().toLowerCase() === id.toString().toLowerCase() ||
               c.symbol?.toLowerCase() === id.toString().toLowerCase()
        );
        if (defaultCoin) {
          setAsset(defaultCoin);
        }
      }
    }
  };

  // Update selected asset price in real-time (especially for XAU and OIL)
  useEffect(() => {
    if (!asset || !selectedAssetId) return;
    
    const symbolKey = asset.symbol?.toUpperCase();
    if (symbolKey === 'XAU' || symbolKey === 'OIL') {
      // Fetch price immediately
      const fetchPrice = async () => {
        const tradingViewSymbol = symbolKey === 'XAU' ? 'FX_IDC:XAUUSD' : 'NYMEX:CL1!';
        try {
          const priceData = await fetchTradingViewPrice(tradingViewSymbol);
          if (priceData && priceData.price) {
            console.log(`Updating ${symbolKey} price to:`, priceData.price);
            setAsset(prevAsset => ({
              ...prevAsset,
              price: priceData.price,
              priceChange: priceData.priceChange
            }));
          } else {
            console.warn(`Failed to fetch price for ${symbolKey}`);
          }
        } catch (error) {
          console.error('Error updating asset price:', error);
        }
      };

      // Fetch immediately
      fetchPrice();
      
      // Then update every 2 seconds
      const interval = setInterval(fetchPrice, 2000);
      
      return () => clearInterval(interval);
    }
  }, [asset?.symbol, selectedAssetId]);

  const handleCoinSelect = (coinId) => {
    navigate(`/trading/${coinId}`);
  };

  const getDurationLabel = (dur) => {
    if (tradeSettings?.[dur]) {
      return tradeSettings[dur].label || dur.toUpperCase();
    }
    const labels = {
      long: '30',
      ultra: '60',
      vip: '120'
    };
    return labels[dur] || dur;
  };

  // Open trade modal
  const openTradeModal = (type) => {
    setTradeType(type);
    setTradeAmount('0'); // Start with 0 as requested
    setShowTradeModal(true);
  };

  // Close trade modal
  const closeTradeModal = () => {
    setShowTradeModal(false);
    setTradeAmount('0'); // Reset to 0
  };

  // Execute trade
  const executeTrade = async () => {
    if (!selectedAssetId) {
      toast.error('กรุณาเลือกสินทรัพย์');
      return;
    }

    // Allow trading without verification - removed verification check
    // if (!user.isVerified) {
    //   toast.error('กรุณายืนยันตัวตนก่อนทำการซื้อขาย');
    //   return;
    // }

    const currentSettings = tradeSettings?.[duration];
    
    if (!currentSettings) {
      toast.error('ประเภทการเทรดไม่ถูกต้อง');
      return;
    }

    const tradeAmountValue = parseFloat(tradeAmount) || 0;
    const minTradeAmount = currentSettings.minTradeAmount || 100;
    const maxTradeAmount = currentSettings.maxTradeAmount || 10000000;
    
    if (tradeAmountValue <= 0) {
      toast.error('กรุณากรอกจำนวนเงิน');
      return;
    }
    
    if (tradeAmountValue < minTradeAmount) {
      toast.error(`จำนวนเงินขั้นต่ำคือ ${minTradeAmount.toLocaleString()} USDT`);
      return;
    }

    if (tradeAmountValue > maxTradeAmount) {
      toast.error(`จำนวนเงินสูงสุดคือ ${maxTradeAmount.toLocaleString()} USDT`);
      return;
    }

    if (tradeAmountValue > user.balance) {
      toast.error('ยอดเงินไม่เพียงพอ');
      return;
    }

    if (!currentSettings.enabled) {
      const roomLabel = currentSettings.label || duration.toUpperCase();
      toast.error(`การเทรดห้อง ${roomLabel} ถูกปิดชั่วคราว`);
      return;
    }

    try {
      setLoading(true);
      const response = await api.placeTrade(selectedAssetId, tradeAmountValue, tradeType, duration);
      
      if (response.success) {
        toast.success(`เปิดการเทรดสำเร็จ (${currentSettings.duration} วินาที)`);
        setActiveTrade(response.trade);
        setCountdown(currentSettings.duration);
        setAmount(tradeAmountValue.toString());
        closeTradeModal();
        await refreshUser();
      }
    } catch (error) {
      console.error('Trade error:', error);
      toast.error(error.message || 'ไม่สามารถเปิดการเทรดได้');
    } finally {
      setLoading(false);
    }
  };

  const handleTrade = async (e) => {
    e.preventDefault();
    // Don't execute trade directly, open modal instead
    openTradeModal(tradeType);
  };

  // If no asset is selected, show coin selection page
  if (!urlAssetId) {
    return (
      <div className="coin-selection-page">
        {/* Header */}
        <div className="page-header">
          <h1>เลือกเหรียญ</h1>
        </div>

        {/* Search Bar */}
        <div className="search-section">
          <div className="search-bar">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search coins..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="selection-tabs">
          {tabs.map((tab) => (
            <Link
              key={tab.id}
              to={tab.link}
              className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            >
              {tab.label}
            </Link>
          ))}
        </div>

        {/* Coin List */}
        <div className="coin-list-container">
          {loading ? (
            <div className="loading-container">
              <div className="spinner" />
            </div>
          ) : (
            <div className="coin-list">
              {filteredAssets.map((coin, index) => (
                <motion.div
                  key={coin.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                  className="coin-row"
                  onClick={() => handleCoinSelect(coin.id)}
                >
                  <div className="coin-left">
                    <div className="coin-icon">
                      <img 
                        src={getAssetImage(coin.symbol)} 
                        alt={coin.name}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="coin-placeholder" style={{ display: 'none' }}>
                        {coin.symbol?.charAt(0)}
                      </div>
                    </div>
                    <span className="coin-symbol">{coin.symbol === 'XAU' ? 'GOLD' : coin.symbol}</span>
                  </div>
                  
                  <div className="coin-right">
                    <div className="coin-price-info">
                      <span className="coin-price">{coin.price?.toLocaleString()}</span>
                      <span className="coin-price-usd">${coin.price?.toLocaleString()}</span>
                    </div>
                    <div className={`coin-change-badge ${coin.priceChange >= 0 ? 'positive' : 'negative'}`}>
                      {coin.priceChange >= 0 ? '+' : ''}{coin.priceChange?.toFixed(2)}%
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Bottom Navigation */}
        <nav className="bottom-nav">
          <Link to="/" className="nav-item">
            <FaHome />
            <span>Home</span>
          </Link>
          <Link to="/trading" className="nav-item active">
            <FaChartBar />
            <span>Trade</span>
          </Link>
          <Link to="/history" className="nav-item">
            <FaEye />
            <span>History</span>
          </Link>
          <Link to="/profile" className="nav-item">
            <FaFileAlt />
            <span>Profile</span>
          </Link>
        </nav>
      </div>
    );
  }

  // Trading View with chart - Full Page
  if (loading && assets.length === 0) {
    return (
      <div className="loading-container">
        <div className="spinner" />
      </div>
    );
  }

  const currentSettings = tradeSettings?.[duration];
  const profitPercent = currentSettings?.profitPercent || 0;
  // ราคาซื้อขายจะอัพเดทแบบ real-time ตาม asset.price ที่อัพเดทจาก useEffect
  const buyPrice = asset?.price ? parseFloat(asset.price).toFixed(2) : '0.00';
  const sellPrice = asset?.price ? parseFloat(asset.price).toFixed(2) : '0.00';

  return (
    <div className="trading-page-full">
      {/* Chart Container */}
      <div className="chart-container-dark-full">
        <div id="tradingview_chart" ref={chartContainerRef}></div>
      </div>

      {/* Trading Form Section - Full Page */}
      <div className="trading-form-section-full">
        <form onSubmit={handleTrade} className="trading-form-dark">
            {/* Asset Info */}
            <div className="asset-info-trade">
              <div className="asset-icon-trade">
                <img 
                  src={getAssetImage(asset?.symbol)} 
                  alt={asset?.name}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="asset-placeholder-trade" style={{ display: 'none' }}>
                  {asset?.symbol?.charAt(0)}
                </div>
              </div>
              <span className="asset-symbol-trade">{asset?.name || `${asset?.symbol === 'XAU' ? 'GOLD' : asset?.symbol || 'N/A'}USD`}</span>
              <div className="trade-options">
                <span>TP/SL</span>
                <span>Info</span>
                <span>Pending</span>
              </div>
            </div>

            {/* Buy/Sell Buttons with Countdown Timer */}
            <div className="trade-buttons-row">
              <button
                type="button"
                className="trade-btn-sell"
                onClick={(e) => {
                  e.preventDefault();
                  openTradeModal('sell');
                }}
                disabled={countdown !== null}
              >
                <div className="trade-btn-label">SELL</div>
              </button>

              <div className="countdown-timer-display">
                <div className="countdown-timer-value">
                  {countdown !== null ? `${countdown}s` : 'เวลาเทรด'}
                </div>
              </div>

              <button
                type="button"
                className="trade-btn-buy"
                onClick={(e) => {
                  e.preventDefault();
                  openTradeModal('buy');
                }}
                disabled={countdown !== null}
              >
                <div className="trade-btn-label">BUY</div>
              </button>
            </div>
          </form>
      </div>

      {/* Trade Modal */}
      {showTradeModal && (
        <div className="trade-modal-overlay" onClick={closeTradeModal}>
          <div className="trade-modal-content" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="trade-modal-header">
              <h3>{asset?.name || `${asset?.symbol === 'XAU' ? 'GOLD' : asset?.symbol || 'N/A'}USD`}</h3>
              <button className="trade-modal-close" onClick={closeTradeModal}>×</button>
            </div>

            {/* Current Price */}
            <div className="trade-modal-price-section">
              <label className="trade-modal-label">ราคา{tradeType === 'buy' ? 'ซื้อ' : 'ขาย'}</label>
              <div className="trade-modal-price-value">
                {asset?.price ? parseFloat(asset.price).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                }) : '0.00'}
              </div>
            </div>

            {/* Max Amount */}
            <div className="trade-modal-info">
              <span className="trade-modal-label">จำนวนสูงสุดที่สามารถใช้ได้ :</span>
              <span className="trade-modal-value">
                {(() => {
                  const roomMaxAmount = tradeSettings?.[duration]?.maxTradeAmount || maxTrade;
                  return roomMaxAmount.toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                  });
                })()} USDT
              </span>
            </div>

            {/* Trade Room Buttons (using Leverage UI) */}
            {tradeSettings && (
              <div className="trade-modal-leverage">
                <label className="trade-modal-label">เลือกห้องเทรด</label>
                <div className="leverage-buttons">
                  {['1x', '2x', '3x', '4x', '5x'].map((roomType) => {
                    const setting = tradeSettings[roomType];
                    if (!setting || !setting.enabled) return null;
                    const label = setting.label || roomType.toUpperCase();
                    return (
                      <button
                        key={roomType}
                        type="button"
                        className={`leverage-btn ${duration === roomType ? 'active' : ''}`}
                        onClick={() => setDuration(roomType)}
                        disabled={!setting.enabled}
                      >
                        {label}
                      </button>
                    );
                  }).filter(Boolean)}
                </div>
              </div>
            )}

            {/* Amount Input */}
            <div className="trade-modal-amount-display">
              <span className="trade-modal-label">จำนวน (USDT)</span>
              <input
                type="number"
                className="trade-modal-amount-input"
                value={tradeAmount}
                onChange={(e) => setTradeAmount(e.target.value)}
                placeholder="0"
                min={tradeSettings?.[duration]?.minTradeAmount || 0}
                max={tradeSettings?.[duration]?.maxTradeAmount || 10000000}
                step="0.01"
              />
            </div>

            {/* Balance */}
            <div className="trade-modal-balance">
              <span className="trade-modal-label">ยอดคงเหลือ:</span>
              <span className="trade-modal-value">
                {(user?.balance || 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })} USDT
              </span>
            </div>

            {/* Buy/Sell Button */}
            <button
              type="button"
              className={`trade-modal-submit-btn ${tradeType === 'buy' ? 'buy' : 'sell'}`}
              onClick={executeTrade}
              disabled={loading}
            >
              {loading ? 'กำลังดำเนินการ...' : tradeType === 'buy' ? 'ซื้อ' : 'ขาย'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Trading;
